import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { exactOption, rankOptions, type ComboboxOption } from '@/lib/comboboxMatch'

export type { ComboboxOption }

/**
 * A text box with a dropdown of suggestions: type to filter, arrow keys and
 * Enter (or a click) to pick. It is a real input, so it type-searches and
 * autofills like any other field, and works inside a modal dialog — the list
 * renders in place (`portal={false}`), within the dialog's focus trap, and
 * flips above the field rather than being cut off at the dialog's edge.
 *
 * Leaving the field commits what was typed when it names an option outright.
 * Anything else is kept as typed with `freeText` (the caller validates it), or
 * reverted to the last choice without it.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  freeText = false,
  autoComplete = 'off',
  invalid,
  describedBy,
  onBlur,
  className,
  maxRender = 100,
  title,
}: {
  id?: string
  /** The chosen option's value — or, with `freeText`, whatever was typed. */
  value: string
  onChange: (value: string) => void
  options: ComboboxOption[]
  placeholder?: string
  freeText?: boolean
  autoComplete?: string
  invalid?: boolean
  describedBy?: string
  onBlur?: () => void
  className?: string
  maxRender?: number
  /** Hover text for the field itself. */
  title?: string
}) {
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const selected = options.find(o => o.value === value)
  const display = selected?.label ?? (freeText ? value : '')
  const [text, setText] = useState(display)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  // While typing, the list filters on the text; just opened, it shows everything.
  const [filtering, setFiltering] = useState(false)
  // The dialog the field sits in, if any: the list's collision boundary.
  const [boundary, setBoundary] = useState<Element | null>(null)
  useEffect(() => {
    if (open) setBoundary(inputRef.current?.closest('[role="dialog"]') ?? null)
  }, [open])

  // Follow a value changed from outside (another country, a reset).
  useEffect(() => {
    if (!open) setText(display)
  }, [display, open])

  const shown = useMemo(
    () => (filtering ? rankOptions(options, text) : options).slice(0, maxRender),
    [filtering, options, text, maxRender],
  )

  useEffect(() => {
    setActive(0)
  }, [text, filtering])

  // Escape with the list open closes the list, not the dialog around it. Radix
  // dismisses a dialog from a capture listener on the document, which the
  // input's own handler runs too late to stop; a window capture listener runs
  // first.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.target !== inputRef.current) return
      e.stopPropagation()
      e.preventDefault()
      setOpen(false)
      setFiltering(false)
      setText(display)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, display])

  // Keep the highlighted option in view when the arrows move past the edge.
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function pick(option: ComboboxOption) {
    onChange(option.value)
    setText(option.label)
    setFiltering(false)
    setOpen(false)
  }

  /** What leaving the field means for the text in it. */
  function commit() {
    setOpen(false)
    setFiltering(false)
    const typed = text.trim()
    if (!typed) {
      if (value) onChange('')
      setText('')
      return
    }
    const match = exactOption(options, typed)
    if (match) {
      if (match.value !== value) onChange(match.value)
      setText(match.label)
    } else if (freeText) {
      if (typed !== value) onChange(typed)
    } else {
      setText(display)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative', className)}>
          <input
            ref={inputRef}
            id={id}
            role='combobox'
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete='list'
            aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            autoComplete={autoComplete}
            value={text}
            placeholder={placeholder}
            className='flex h-10 w-full rounded-md border border-input bg-background py-2 pl-3 pr-9 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-[invalid=true]:border-destructive md:text-sm'
            onChange={e => {
              setText(e.target.value)
              setFiltering(true)
              setOpen(true)
            }}
            onClick={e => {
              // Reopening an already-focused field: start over, as on focus.
              if (!open) e.currentTarget.select()
              setOpen(true)
            }}
            // Select the current choice so typing replaces it. After the click's
            // own mouseup, which would otherwise put the caret back and deselect.
            onFocus={e => {
              const input = e.currentTarget
              setTimeout(() => input.select(), 0)
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault()
                if (!open) return setOpen(true)
                const step = e.key === 'ArrowDown' ? 1 : -1
                setActive(i => Math.min(Math.max(i + step, 0), Math.max(shown.length - 1, 0)))
              } else if (e.key === 'Enter') {
                if (open && shown[active]) {
                  e.preventDefault()
                  pick(shown[active])
                }
              } else if (e.key === 'Tab' && open && filtering && shown[active] && !exactOption(options, text)) {
                // Tab takes the highlighted suggestion, as Enter does, and moves on.
                pick(shown[active])
              }
            }}
            title={title}
            onBlur={e => {
              // A click in the list is not leaving the field.
              if (listRef.current?.contains(e.relatedTarget as Node | null)) return
              commit()
              onBlur?.()
            }}
          />
          <ChevronDown
            className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50'
            aria-hidden
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        portal={false}
        align='start'
        // Inside a dialog the list renders in place, so it must stay within the
        // dialog's box: it flips above the field when there is no room below.
        collisionBoundary={boundary}
        collisionPadding={8}
        className='w-[var(--radix-popover-trigger-width)] min-w-[12rem] p-1'
        style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
        onOpenAutoFocus={e => e.preventDefault()}
        onCloseAutoFocus={e => e.preventDefault()}
        onInteractOutside={e => {
          if (inputRef.current?.contains(e.target as Node)) e.preventDefault()
        }}
      >
        <div
          ref={listRef}
          id={listId}
          role='listbox'
          tabIndex={-1}
          className='max-h-[min(15rem,calc(var(--radix-popover-content-available-height)-0.5rem))] overflow-y-auto'
        >
          {shown.length === 0 ? (
            <p className='px-2 py-1.5 text-sm text-muted-foreground'>
              {freeText ? 'No match — what you typed is kept.' : 'No matches.'}
            </p>
          ) : (
            shown.map((o, i) => (
              <div
                key={o.value}
                id={`${listId}-${i}`}
                data-index={i}
                role='option'
                aria-selected={o.value === value}
                title={o.hint}
                className={cn(
                  'cursor-pointer rounded-sm px-2 py-1.5 text-sm',
                  i === active && 'bg-accent text-accent-foreground',
                  o.value === value && 'font-medium',
                )}
                onMouseEnter={() => setActive(i)}
                // Before the input's blur, so the pick wins over the commit.
                onMouseDown={e => {
                  e.preventDefault()
                  pick(o)
                }}
              >
                {o.label}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
